import React, { useState } from 'react';
import { detectVideoUrl, validateVideoUrl, getPlatformDisplayName } from '../utils/videoUtils';

const PostCreator = ({ onPostCreated, currentUser }) => {
  const [postContent, setPostContent] = useState('');
  const [postType, setPostType] = useState('status');
  const [visibility, setVisibility] = useState('network');
  const [mediaUrl, setMediaUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showMediaInput, setShowMediaInput] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaType, setMediaType] = useState('url'); // 'url' or 'upload'

  const postTypeOptions = [
    { value: 'status', label: 'Status Update', icon: '💭' },
    { value: 'job_update', label: 'Job Update', icon: '💼' },
    { value: 'achievement', label: 'Achievement', icon: '🏆' },
    { value: 'article_share', label: 'Article Share', icon: '📰' }
  ];

  // Filter visibility options based on permissions
  const getVisibilityOptions = () => {
    const allOptions = [
      { value: 'public', label: 'Public', description: 'Anyone can see this post' },
      { value: 'network', label: 'Network', description: 'Only your connections can see this' },
      { value: 'private', label: 'Private', description: 'Only you can see this post' }
    ];

    // If user doesn't have can_post_to_network permission, remove 'public' option
    if (currentUser && !currentUser.can_post_to_network) {
      return allOptions.filter(opt => opt.value !== 'public');
    }

    return allOptions;
  };

  const visibilityOptions = getVisibilityOptions();

  // Ensure visibility is valid for user's permissions
  React.useEffect(() => {
    if (currentUser && !currentUser.can_post_to_network && visibility === 'public') {
      setVisibility('network'); // Default to network if they can't post publicly
    }
  }, [currentUser, visibility]);

  const handleMediaUrlChange = (e) => {
    const url = e.target.value;
    setMediaUrl(url);
    setError('');

    if (url.trim()) {
      const validation = validateVideoUrl(url);
      if (validation.isValid) {
        setVideoPreview(validation.videoInfo);
      } else {
        setVideoPreview(null);
        // Don't show error immediately while typing
      }
    } else {
      setVideoPreview(null);
    }
  };

  const handleMediaUrlBlur = () => {
    if (mediaUrl.trim()) {
      const validation = validateVideoUrl(mediaUrl);
      if (!validation.isValid) {
        setError(validation.error);
      } else {
        setMediaUrl(validation.normalizedUrl);
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/alumni/upload-post-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to upload image');
      }

      // Store the uploaded image data
      setUploadedImage({
        url: data.image_url,
        s3_key: data.s3_key,
        filename: file.name
      });

      // Clear any existing URL media
      setMediaUrl('');
      setVideoPreview(null);
      setMediaType('upload');

    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setMediaType('url');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!postContent.trim()) {
      setError('Post content is required');
      return;
    }

    // Validate URL media if present
    if (mediaType === 'url' && mediaUrl.trim()) {
      const validation = validateVideoUrl(mediaUrl);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const token = localStorage.getItem('token');

      // Determine media URL and type to send
      let finalMediaUrl = null;
      let finalMediaType = null;

      if (mediaType === 'upload' && uploadedImage) {
        finalMediaUrl = uploadedImage.s3_key; // Store S3 key, not presigned URL
        finalMediaType = 'image';
      } else if (mediaType === 'url' && mediaUrl.trim()) {
        finalMediaUrl = mediaUrl.trim();
        finalMediaType = 'link'; // URLs are treated as links
      }

      const response = await fetch(`${backendUrl}/api/alumni/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: postContent.trim(),
          post_type: postType,
          media_url: finalMediaUrl,
          media_type: finalMediaType,
          visibility: visibility
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create post');
      }

      // Reset form
      setPostContent('');
      setMediaUrl('');
      setVideoPreview(null);
      setUploadedImage(null);
      setShowMediaInput(false);
      setPostType('status');
      setVisibility('network');
      setMediaType('url');

      // Notify parent component
      if (onPostCreated) {
        onPostCreated(data);
      }

    } catch (err) {
      console.error('Error creating post:', err);
      setError(err.message || 'Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderVideoPreview = () => {
    if (!videoPreview) return null;

    return (
      <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center space-x-2 mb-2">
          <div className="text-sm font-medium text-gray-700">
            {getPlatformDisplayName(videoPreview.platform)} Video Preview
          </div>
          <button
            type="button"
            onClick={() => {
              setMediaUrl('');
              setVideoPreview(null);
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          <iframe
            src={videoPreview.embedUrl}
            title="Video Preview"
            className="w-full h-full"
            frameBorder="0"
            allowFullScreen
          />
        </div>
        
        <div className="mt-2 text-xs text-gray-500">
          Video will be embedded in your post
        </div>
      </div>
    );
  };

  const renderImagePreview = () => {
    if (!uploadedImage) return null;

    return (
      <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-700">
            📸 Uploaded Image
          </div>
          <button
            type="button"
            onClick={removeUploadedImage}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        
        <div className="rounded-lg overflow-hidden bg-white">
          <img
            src={uploadedImage.url}
            alt="Uploaded preview"
            className="w-full max-h-64 object-contain"
          />
        </div>
        
        <div className="mt-2 text-xs text-gray-500">
          {uploadedImage.filename} • Image will be attached to your post
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-3 sm:p-6 mb-4 sm:mb-6">
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* User Avatar and Content Input */}
        <div className="flex items-start space-x-2 sm:space-x-3">
          <div className="flex-shrink-0">
            {currentUser?.headshot_url ? (
              <img
                src={currentUser.headshot_url}
                alt={`${currentUser.first_name} ${currentUser.last_name}`}
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover"
              />
            ) : (
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-xs sm:text-sm">
                {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0]}
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="Share an update with your network..."
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              rows={2}
              maxLength={2000}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {postContent.length}/2000 characters
            </div>
          </div>
        </div>

        {/* Media Input Section */}
        {showMediaInput && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Add Media
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowMediaInput(false);
                  setMediaUrl('');
                  setVideoPreview(null);
                  setUploadedImage(null);
                  setMediaType('url');
                }}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                Remove
              </button>
            </div>

            {/* Media Type Selector */}
            <div className="flex space-x-4 mb-3">
              <button
                type="button"
                onClick={() => {
                  setMediaType('upload');
                  setMediaUrl('');
                  setVideoPreview(null);
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  mediaType === 'upload'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                📸 Upload Image
              </button>
              <button
                type="button"
                onClick={() => {
                  setMediaType('url');
                  setUploadedImage(null);
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  mediaType === 'url'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                🔗 Add URL
              </button>
            </div>

            {/* Image Upload Interface */}
            {mediaType === 'upload' && (
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="text-xs text-gray-500">
                  Supported formats: JPG, PNG, GIF, WebP • Max size: 5MB
                </div>
                {isUploading && (
                  <div className="text-sm text-blue-600">
                    Uploading image...
                  </div>
                )}
                {renderImagePreview()}
              </div>
            )}

            {/* URL Input Interface */}
            {mediaType === 'url' && (
              <div className="space-y-2">
                <input
                  type="url"
                  value={mediaUrl}
                  onChange={handleMediaUrlChange}
                  onBlur={handleMediaUrlBlur}
                  placeholder="https://youtube.com/watch?v=... or https://example.com/image.jpg"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
                <div className="text-xs text-gray-500">
                  Supports YouTube, Vimeo, Dailymotion, Twitch videos and direct image links
                </div>
                {renderVideoPreview()}
              </div>
            )}
          </div>
        )}

        {/* Post Options */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            {/* Post Type Selector */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <label className="text-xs sm:text-sm font-medium text-gray-700">Type:</label>
              <select
                value={postType}
                onChange={(e) => setPostType(e.target.value)}
                className="text-xs sm:text-sm border border-gray-300 rounded-md px-1 sm:px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {postTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Visibility Selector */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <label className="text-xs sm:text-sm font-medium text-gray-700">Visibility:</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="text-xs sm:text-sm border border-gray-300 rounded-md px-1 sm:px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                title={visibilityOptions.find(opt => opt.value === visibility)?.description}
              >
                {visibilityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Media Button */}
            {!showMediaInput && (
              <button
                type="button"
                onClick={() => setShowMediaInput(true)}
                className="flex items-center space-x-1 text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                <span>📎</span>
                <span className="hidden sm:inline">Add Media</span>
                <span className="sm:hidden">Media</span>
              </button>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!postContent.trim() || isSubmitting}
            className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm ${
              !postContent.trim() || isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Posting...' : 'Share Update'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostCreator;